import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  InputAdornment,
} from '@mui/material';
import { AccountBalance, Add, Delete, Save, Cancel, LocalOffer, Search, CreditCard, Payments, Visibility } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import API from '../../services/api';
import CustomSnackBar from '../../components/CustomSnackBar';
import CustomModal from '../../components/CustomModal';
import AddProductToInvoice from './AddProductToInvoice';
import AddBonusToInvoice from './AddBonusToInvoice';
import { normalizeInvoicePaymentStatus } from '../../utils/paymentStatus';

const toISO = (v) => {
  if (!v) return '';
  if (typeof v === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
    if (v.includes('T')) return v.split('T')[0];
  }
  try {
    const d = new Date(v);
    if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];
  } catch {}
  return '';
};

const normalizePaymentType = (value) => {
  const raw = String(value ?? '').trim().toLowerCase();
  if (['credito', 'crédito', 'credit', 'true', '1'].includes(raw)) return 'Credito';
  return 'Contado';
};

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const BANK_REQUISITION_FLOW = 'BANK_REQUISITION';

const bankPaymentStatusLabels = {
  PENDING_REQUISITION: 'Pendiente Abasto',
  PENDING_BANK_CAPTURE: 'Pendiente captura bancaria',
  CAPTURED: 'Capturada',
  PARTIALLY_PAID_BANK: 'Pago bancario parcial',
  PAID_BANK: 'Pagada por banco',
};

const abastoStatusColors = {
  PENDING: 'warning',
  PARTIALLY_PAID: 'info',
  PAID: 'success',
  OVERDUE: 'error',
  CANCELLED: 'default',
};

const formatDateLabel = (value) => {
  if (!value) return '-';
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return `${text.slice(8, 10)}/${text.slice(5, 7)}/${text.slice(0, 4)}`;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('es-GT');
};

export default function EditInvoice({ invoiceId, setOpen, onSaved }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });
  const [invoice, setInvoice] = useState(null);
  const [header, setHeader] = useState({ number: '', series: '', paymentType: '', date: '' });
  const [details, setDetails] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [modifiedDetails, setModifiedDetails] = useState({});
  const [modifiedBonuses, setModifiedBonuses] = useState({});
  const [deletedDetails, setDeletedDetails] = useState([]);
  const [deletedBonuses, setDeletedBonuses] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [openAddBonus, setOpenAddBonus] = useState(false);
  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [formats, setFormats] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [abastoTrace, setAbastoTrace] = useState({ requisition: null, obligation: null, loading: false });

  const fetchInvoice = useCallback(async () => {
    if (!invoiceId) return;
    try {
      const res = await API.get(`invoice/${invoiceId}/`);
      const inv = normalizeInvoicePaymentStatus(res.data);
      if (inv?.invoice_type?.id && !inv.invoice_type_id) {
        inv.invoice_type_id = inv.invoice_type.id;
      }
      setInvoice(inv);
      setHeader({
        number: inv.number ?? '',
        series: inv.series ?? '',
        paymentType: inv.paymentType ?? '',
        date: toISO(inv.date) || toISO(inv.createdAt) || '',
        invoice_type_id: inv.invoice_type_id || inv.invoice_type?.id || '',
      });
      const det = (inv.invoice_detail || []).map(d => ({
        ...d,
        prformat_id: d?.prformat_id ?? d?.prformat?.id ?? null,
      }));
      const bon = (inv.invoice_bonus || []).map(b => ({
        ...b,
        prformat_id: b?.prformat_id ?? b?.prformat?.id ?? null,
      }));
      setDetails(det);
      setBonuses(bon);
      setModifiedDetails({});
      setModifiedBonuses({});
      setDeletedDetails([]);
      setDeletedBonuses([]);
      setSearchTerm('');
      setDebouncedSearch('');
    } catch (e) {
      console.error('Error fetching invoice', e);
      setSnack({ open: true, message: 'Error cargando factura', severity: 'error' });
    }
  }, [invoiceId]);

  useEffect(() => { fetchInvoice(); }, [fetchInvoice]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get('invoice/catalogs/create_invoice/');
        setInvoiceTypes(Array.isArray(data?.invoice_types) ? data.invoice_types : []);
        setFormats(Array.isArray(data?.formats) ? data.formats : []);
      } catch {
        setInvoiceTypes([]);
        setFormats([]);
      }
    })();
  }, []);

  const handleHeaderChange = (field, value) =>
    setHeader(prev => ({ ...prev, [field]: value }));

  const onDetailChange = (id, field, value) => {
    setDetails(prev => prev.map(d => d.id === id ? { ...d, [field]: value } : d));
    setModifiedDetails(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };
  const onBonusChange = (id, field, value) => {
    setBonuses(prev => prev.map(b => b.id === id ? { ...b, [field]: value } : b));
    setModifiedBonuses(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const handleDeleteDetail = (id) => {
    setDeletedDetails(prev => prev.includes(id) ? prev : [...prev, id]);
    setDetails(prev => prev.filter(d => d.id !== id));
    const next = { ...modifiedDetails }; delete next[id]; setModifiedDetails(next);
  };
  const handleDeleteBonus = (id) => {
    setDeletedBonuses(prev => prev.includes(id) ? prev : [...prev, id]);
    setBonuses(prev => prev.filter(b => b.id !== id));
    const next = { ...modifiedBonuses }; delete next[id]; setModifiedBonuses(next);
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 280);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const isCreditPayment = useMemo(
    () => normalizePaymentType(header.paymentType) === 'Credito',
    [header.paymentType]
  );

  const combinedRows = useMemo(() => ([
    ...details.map(d => ({ type: 'detail', data: d })),
    ...bonuses.map(b => ({ type: 'bonus', data: b })),
  ]), [details, bonuses]);

  const filteredRows = useMemo(() => {
    const term = String(debouncedSearch || '').trim().toLowerCase();
    if (!term) return combinedRows;
    return combinedRows.filter((row) => {
      const d = row.data || {};
      const code = String(d.product?.code ?? d.product_id ?? '').toLowerCase();
      const description = String(d.product?.description ?? d.description ?? '').toLowerCase();
      return code.includes(term) || description.includes(term);
    });
  }, [combinedRows, debouncedSearch]);

  const totalRealtime = useMemo(() => {
    const cents = (details || []).reduce((sum, d) => {
      return sum + Math.round(toNumber(d?.quantity) * toNumber(d?.price) * 100);
    }, 0);
    return cents / 100;
  }, [details]);

  const invoiceAdjustmentValue = useMemo(
    () => toNumber(invoice?.invoice_adjustment),
    [invoice?.invoice_adjustment]
  );

  const totalRealtimeWithAdjustment = useMemo(
    () => Math.round((totalRealtime + invoiceAdjustmentValue) * 100) / 100,
    [totalRealtime, invoiceAdjustmentValue]
  );

  const isVoided = useMemo(
    () => String(invoice?.lifecycle_status || '').toUpperCase() === 'VOID',
    [invoice?.lifecycle_status]
  );
  const isAbastoInvoice = useMemo(
    () => String(invoice?.payment_flow_type || '').toUpperCase() === BANK_REQUISITION_FLOW,
    [invoice?.payment_flow_type]
  );
  const readOnly = loading || isVoided;

  const formatCurrency = useCallback((value) => (
    new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ' }).format(toNumber(value))
  ), []);

  useEffect(() => {
    if (!isAbastoInvoice || !invoice) {
      setAbastoTrace({ requisition: null, obligation: null, loading: false });
      return undefined;
    }

    const requisitionId = typeof invoice.bank_requisition === 'object'
      ? invoice.bank_requisition?.id
      : (invoice.bank_requisition || invoice.bank_requisition_id);
    const obligationId = invoice.abasto_obligation_id;
    if (!requisitionId && !obligationId) {
      setAbastoTrace({ requisition: null, obligation: null, loading: false });
      return undefined;
    }

    let mounted = true;
    setAbastoTrace((prev) => ({ ...prev, loading: true }));
    Promise.all([
      requisitionId ? API.get(`bank-requisition/${requisitionId}/`).then(({ data }) => data).catch(() => null) : Promise.resolve(null),
      obligationId ? API.get(`abasto-obligations/${obligationId}/`).then(({ data }) => data).catch(() => null) : Promise.resolve(null),
    ]).then(([requisition, obligation]) => {
      if (!mounted) return;
      setAbastoTrace({ requisition, obligation, loading: false });
    });

    return () => {
      mounted = false;
    };
  }, [invoice, isAbastoInvoice]);

  const saveAll = async () => {
    if (!invoiceId) return;
    if (isVoided) {
      setSnack({ open: true, message: 'La factura anulada no puede editarse', severity: 'warning' });
      return;
    }
    setLoading(true);
    try {
      await API.patch(`invoice/${invoiceId}/`, {
        number: header.number === '' ? null : header.number,
        series: header.series,
        paymentType: header.paymentType,
        date: header.date || null,
        invoice_type_id: header.invoice_type_id || null,
        total: totalRealtimeWithAdjustment,
      });

      await Promise.all([
        ...deletedDetails.map(id => API.delete(`invoice-detail/${id}/`).catch(() => {})),
        ...deletedBonuses.map(id => API.delete(`invoice-bonus/${id}/`).catch(() => {})),
      ]);

      await Promise.all([
        ...Object.entries(modifiedDetails).map(([id, changes]) => {
          const payload = {};
          if (changes.quantity != null) payload.quantity = Number(changes.quantity);
          if (changes.quantityRe != null) payload.quantityRe = Number(changes.quantityRe);
          if (changes.price != null) payload.price = Number(changes.price);
          if (changes.prformat_id != null) payload.prformat_id = changes.prformat_id;
          if (changes.dueDate != null) payload.dueDate = toISO(changes.dueDate);
          if (Object.keys(payload).length === 0) return Promise.resolve();
          return API.patch(`invoice-detail/${id}/`, payload);
        }),
        ...Object.entries(modifiedBonuses).map(([id, changes]) => {
          const payload = {};
          if (changes.quantity != null) payload.quantity = Number(changes.quantity);
          if (changes.quantityRe != null) payload.quantityRe = Number(changes.quantityRe);
          if (changes.prformat_id != null) payload.prformat_id = changes.prformat_id;
          if (changes.comment != null) payload.comment = String(changes.comment);
          if (Object.keys(payload).length === 0) return Promise.resolve();
          return API.patch(`invoice-bonus/${id}/`, payload);
        }),
      ]);

      setSnack({ open: true, message: 'Factura actualizada', severity: 'success' });
      setTimeout(async () => {
        if (onSaved) await onSaved();
        setOpen(false);
      }, 1500);
    } catch (e) {
      console.error(e);
      setSnack({ open: true, message: 'Error al guardar cambios', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (!invoice) {
    return (
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">Cargando...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* ── Datos generales ── */}
      {isAbastoInvoice && (
        <Box sx={{ px: 2.5, py: 1.25, borderBottom: '0.5px solid', borderColor: 'divider', flexShrink: 0 }}>
          <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1, bgcolor: 'rgba(16, 23, 60, 0.03)' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
              <Stack direction="row" spacing={1} alignItems="center">
                <AccountBalance sx={{ color: '#10173c' }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Abasto
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {abastoTrace.loading ? 'Cargando...' : (abastoTrace.requisition?.id || abastoTrace.obligation?.id ? 'Trazabilidad Abasto' : 'Abasto pendiente de cargar')}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                {(invoice.abasto_obligation_status_label || abastoTrace.obligation?.status_label) && (
                  <Chip
                    size="small"
                    label={invoice.abasto_obligation_status_label || abastoTrace.obligation?.status_label}
                    color={abastoStatusColors[invoice.abasto_obligation_status || abastoTrace.obligation?.internal_status] || 'default'}
                    variant="outlined"
                  />
                )}
                <Button
                  size="small"
                  startIcon={<Visibility />}
                  onClick={() => {
                    setOpen(false);
                    navigate('/abasto');
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  Ver Abasto
                </Button>
              </Stack>
            </Stack>

            <Grid container spacing={1.25} sx={{ mt: 0.75 }}>
              {(() => {
                const requisition = abastoTrace.requisition;
                const obligation = abastoTrace.obligation;
                const providerName = (
                  invoice?.supplier?.companyName
                  || invoice?.supplier?.legalName
                  || invoice?.supplier_name
                  || (invoice?.supplier_id ? `Proveedor ${invoice.supplier_id}` : '-')
                );
                const bankBalance = obligation?.balance ?? invoice?.abasto_obligation_balance;
                return [
                  ['Proveedor', providerName],
                  ['Orden relacionada', requisition?.purchase_order_code || (invoice?.order_ids?.length ? invoice.order_ids.join(', ') : '-')],
                  ['No. requisicion', requisition?.requisition_number || obligation?.requisition_number || '-'],
                  ['Monto requisicion', requisition?.requested_amount ? formatCurrency(requisition.requested_amount) : '-'],
                  ['Estado Abasto', invoice.abasto_obligation_status_label || obligation?.status_label || bankPaymentStatusLabels[invoice?.bank_payment_status] || '-'],
                  ['Obligacion/despacho', obligation?.dispatch_number || (invoice.abasto_obligation_id ? `Obligacion ${invoice.abasto_obligation_id}` : '-')],
                  ['Saldo bancario', bankBalance !== null && bankBalance !== undefined ? formatCurrency(bankBalance) : '-'],
                  ['Fecha Abasto', formatDateLabel(requisition?.requisition_date)],
                ];
              })().map(([label, value]) => (
                <Grid item xs={12} sm={6} md={3} key={label}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Box>
      )}

      <Box sx={{
        px: 2.5, pt: 2, pb: 1.5,
        borderBottom: '0.5px solid',
        borderColor: 'divider',
        flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Typography sx={{ fontSize: '11px', fontWeight: 500, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Datos generales
          </Typography>
          {isVoided && (
            <Chip size="small" color="error" variant="outlined" label="Factura anulada · solo lectura" sx={{ fontSize: '11px', height: 20 }} />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField
            label="Número"
            size="small"
            type="number"
            value={header.number ?? ''}
            disabled={readOnly}
            onChange={(e) => handleHeaderChange('number', e.target.value === '' ? '' : Number(e.target.value))}
            sx={{ width: 90 }}
          />
          <TextField
            label="Serie"
            size="small"
            value={header.series}
            disabled={readOnly}
            onChange={(e) => handleHeaderChange('series', e.target.value)}
            sx={{ width: 90 }}
          />
          <TextField
            label="Fecha"
            type="date"
            size="small"
            value={header.date || ''}
            disabled={readOnly}
            onChange={(e) => handleHeaderChange('date', e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 150 }}
          />
          <FormControl size="small" sx={{ width: 180 }}>
            <InputLabel>Tipo de factura</InputLabel>
            <Select
              label="Tipo de factura"
              value={header.invoice_type_id || ''}
              disabled={readOnly}
              onChange={(e) => handleHeaderChange('invoice_type_id', e.target.value)}
            >
              <MenuItem value="">Sin tipo</MenuItem>
              {invoiceTypes.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Toggle crédito / contado */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>Tipo de pago</Typography>
            <Box sx={{
              display: 'inline-flex',
              border: '0.5px solid',
              borderColor: 'divider',
              borderRadius: '6px',
              overflow: 'hidden',
              opacity: readOnly ? 0.5 : 1,
              pointerEvents: readOnly ? 'none' : 'auto',
            }}>
              <Box
                onClick={() => handleHeaderChange('paymentType', 'Credito')}
                sx={{
                  display: 'flex', alignItems: 'center', gap: '5px',
                  px: 1.5, height: '38px',
                  fontSize: '12px', fontWeight: 500,
                  cursor: 'pointer',
                  bgcolor: isCreditPayment ? '#E6F1FB' : 'transparent',
                  color: isCreditPayment ? '#185FA5' : 'text.secondary',
                  transition: 'all 0.15s',
                  userSelect: 'none',
                }}
              >
                <CreditCard sx={{ fontSize: 14 }} />
                Crédito
              </Box>
              <Box sx={{ width: '0.5px', bgcolor: 'divider', flexShrink: 0 }} />
              <Box
                onClick={() => handleHeaderChange('paymentType', 'Contado')}
                sx={{
                  display: 'flex', alignItems: 'center', gap: '5px',
                  px: 1.5, height: '38px',
                  fontSize: '12px', fontWeight: 500,
                  cursor: 'pointer',
                  bgcolor: !isCreditPayment ? '#E1F5EE' : 'transparent',
                  color: !isCreditPayment ? '#0F6E56' : 'text.secondary',
                  transition: 'all 0.15s',
                  userSelect: 'none',
                }}
              >
                <Payments sx={{ fontSize: 14 }} />
                Contado
              </Box>
            </Box>
          </Box>

          {/* Total */}
          <Box sx={{
            ml: 'auto',
            px: 1.5, py: 0.5,
            bgcolor: 'action.hover',
            borderRadius: '6px',
            display: 'flex', flexDirection: 'column', gap: '2px',
          }}>
            <Typography sx={{ fontSize: '10px', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total</Typography>
            <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>
              {formatCurrency(totalRealtimeWithAdjustment)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Barra de herramientas tabla ── */}
      <Box sx={{
        px: 2.5, py: 1,
        display: 'flex', alignItems: 'center', gap: 1,
        borderBottom: '0.5px solid', borderColor: 'divider',
        flexShrink: 0,
      }}>
        <TextField
          size="small"
          placeholder="Buscar por código o descripción..."
          value={searchTerm}
          disabled={readOnly}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 16, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />

        <Chip
          size="small"
          icon={<LocalOffer sx={{ fontSize: '12px !important' }} />}
          label={`${details.length} productos`}
          sx={{ fontSize: '11px', height: 24, bgcolor: 'action.hover' }}
        />
        <Chip
          size="small"
          icon={<LocalOffer sx={{ fontSize: '12px !important' }} />}
          label={`${bonuses.length} bonificaciones`}
          sx={{ fontSize: '11px', height: 24, bgcolor: '#E1F5EE', color: '#0F6E56', '& .MuiChip-icon': { color: '#0F6E56' } }}
        />

        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<LocalOffer sx={{ fontSize: 14 }} />}
            onClick={() => setOpenAddBonus(true)}
            disabled={readOnly}
            sx={{ fontSize: '12px', height: 32 }}
          >
            Agregar bonificación
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<Add sx={{ fontSize: 14 }} />}
            onClick={() => setOpenAdd(true)}
            disabled={readOnly}
            sx={{ fontSize: '12px', height: 32, bgcolor: '#10173c', '&:hover': { bgcolor: '#1a2456' } }}
          >
            Agregar producto
          </Button>
        </Box>
      </Box>

      {/* ── Tabla ── */}
      <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
        <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 90, fontSize: '11px', fontWeight: 500, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Código</TableCell>
              <TableCell sx={{ fontSize: '11px', fontWeight: 500, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Descripción</TableCell>
              <TableCell align="right" sx={{ width: 90, fontSize: '11px', fontWeight: 500, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Cantidad</TableCell>
              <TableCell align="right" sx={{ width: 90, fontSize: '11px', fontWeight: 500, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Recibida</TableCell>
              <TableCell align="right" sx={{ width: 110, fontSize: '11px', fontWeight: 500, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Precio</TableCell>
              <TableCell sx={{ width: 130, fontSize: '11px', fontWeight: 500, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Formato</TableCell>
              <TableCell sx={{ width: 44 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.disabled', fontSize: '13px' }}>
                  Sin productos ni bonificaciones
                </TableCell>
              </TableRow>
            ) : (
              filteredRows.map((row) => {
                const isBonus = row.type === 'bonus';
                const d = row.data;
                return (
                  <TableRow
                    key={`${isBonus ? 'B' : 'D'}-${d.id}`}
                    hover
                    sx={isBonus ? {
                      bgcolor: '#f4fdf7',
                      '&:hover': { bgcolor: '#eaf8f1 !important' },
                    } : {}}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: '11px', color: 'text.secondary' }}>
                          {d.product?.code || d.product_id}
                        </Typography>
                        {isBonus && (
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: '3px',
                            fontSize: '10px', fontWeight: 500,
                            color: '#0F6E56', bgcolor: '#E1F5EE',
                            px: '6px', py: '1px', borderRadius: '20px',
                          }}>
                            <LocalOffer sx={{ fontSize: 10 }} />
                            Bono
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '12px' }}>
                        {d.product?.description || d.description || ''}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={d.quantity}
                        disabled={readOnly}
                        onChange={(e) => isBonus
                          ? onBonusChange(d.id, 'quantity', e.target.value)
                          : onDetailChange(d.id, 'quantity', e.target.value)
                        }
                        sx={{ width: 72, '& input': { textAlign: 'right', fontSize: '12px' } }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={d.quantityRe}
                        disabled={readOnly}
                        onChange={(e) => isBonus
                          ? onBonusChange(d.id, 'quantityRe', e.target.value)
                          : onDetailChange(d.id, 'quantityRe', e.target.value)
                        }
                        sx={{ width: 72, '& input': { textAlign: 'right', fontSize: '12px' } }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {isBonus ? (
                        <Typography sx={{ color: 'text.disabled', fontSize: '13px' }}>—</Typography>
                      ) : (
                        <TextField
                          size="small"
                          type="number"
                          value={d.price}
                          disabled={readOnly}
                          onChange={(e) => onDetailChange(d.id, 'price', e.target.value)}
                          sx={{ width: 90, '& input': { textAlign: 'right', fontSize: '12px' } }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <TextField
                        select
                        size="small"
                        value={d.prformat_id || ''}
                        disabled={readOnly}
                        onChange={(e) => {
                          const id = e.target.value || null;
                          const fmt = formats.find(f => String(f.id) === String(id));
                          const name = fmt?.name || '';
                          if (isBonus) {
                            onBonusChange(d.id, 'prformat_id', id);
                            onBonusChange(d.id, 'prformat', name);
                          } else {
                            onDetailChange(d.id, 'prformat_id', id);
                            onDetailChange(d.id, 'prformat', name);
                          }
                        }}
                        sx={{ width: '100%', fontSize: '12px' }}
                      >
                        <MenuItem value="">Sin formato</MenuItem>
                        {formats.map((f) => (
                          <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={isBonus ? 'Eliminar bonificación' : 'Eliminar producto'}>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={readOnly}
                          onClick={() => isBonus ? handleDeleteBonus(d.id) : handleDeleteDetail(d.id)}
                          sx={{ width: 28, height: 28 }}
                        >
                          <Delete sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Footer ── */}
      <Box sx={{
        px: 2.5, py: 1.5,
        borderTop: '0.5px solid', borderColor: 'divider',
        display: 'flex', justifyContent: 'flex-end', gap: 1,
        flexShrink: 0,
      }}>
        <Button
          variant="outlined"
          startIcon={<Cancel sx={{ fontSize: 14 }} />}
          onClick={() => setOpen(false)}
          disabled={loading}
          sx={{ fontSize: '13px', height: 36 }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          startIcon={<Save sx={{ fontSize: 14 }} />}
          onClick={saveAll}
          disabled={readOnly}
          sx={{ fontSize: '13px', height: 36, bgcolor: '#10173c', '&:hover': { bgcolor: '#1a2456' } }}
        >
          {loading ? 'Guardando...' : 'Guardar cambios'}
        </Button>
      </Box>

      <CustomSnackBar
        open={snack.open}
        setOpen={(v) => setSnack(prev => ({ ...prev, open: v }))}
        message={snack.message}
        severity={snack.severity}
      />

      <CustomModal
        open={openAdd}
        setOpen={setOpenAdd}
        handleClose={() => setOpenAdd(false)}
        component={AddProductToInvoice}
        width="70%"
        title="Agregar producto a factura"
        icon={Add}
        componentProps={{
          invoiceId,
          open: openAdd,
          disabled: readOnly,
          supplierId: invoice?.supplier_id || invoice?.supplier?.id || null,
          companyId: invoice?.company_id || invoice?.company?.id || null,
          orderIds: (invoice?.order_ids || []),
          onAdded: async () => { await fetchInvoice(); },
        }}
      />

      <CustomModal
        open={openAddBonus}
        setOpen={setOpenAddBonus}
        handleClose={() => setOpenAddBonus(false)}
        component={AddBonusToInvoice}
        width="70%"
        title="Agregar bonificación a factura"
        icon={LocalOffer}
        componentProps={{
          invoiceId,
          open: openAddBonus,
          disabled: readOnly,
          supplierId: invoice?.supplier_id || invoice?.supplier?.id || null,
          companyId: invoice?.company_id || invoice?.company?.id || null,
          orderIds: (invoice?.order_ids || []),
          onAdded: async () => { await fetchInvoice(); },
        }}
      />
    </Box>
  );
}
